import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, createBlock as _createBlock, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = { class: "cc-dialog" };
const _hoisted_2 = { class: "cc-header" };
const _hoisted_3 = { class: "cc-title" };
const _hoisted_4 = { class: "cc-body" };
const _hoisted_5 = {
    key: 0,
    class: "cc-confirm-body"
};
const _hoisted_6 = { class: "cc-confirm-question" };
const _hoisted_7 = { class: "cc-confirm-buttons" };
const _hoisted_8 = { class: "cc-categories" };
const _hoisted_9 = ["onClick"];
const _hoisted_10 = ["src"];
const _hoisted_11 = {
    key: 2,
    class: "cc-check-badge"
};
const _hoisted_12 = {
    key: 0,
    class: "cc-tags-box"
};
const _hoisted_13 = { class: "cc-tags-wrap" };
const _hoisted_14 = ["onClick"];
const _hoisted_15 = {
    key: 0,
    class: "cc-footer"
};
const _hoisted_16 = { class: "cc-toggles" };
const _hoisted_17 = ["disabled"];
import { ref, computed, watch, onMounted } from 'vue';
import { getTransactionClassificationTree } from '@/composables/useSpendingAnalysis';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ChangeCategoryDialog',
    props: {
        merchantName: {},
        currentClassification: {},
        currentExcludeFromAnalysis: { type: Boolean }
    },
    emits: ["apply"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const isOpen = ref(false);
        const saving = ref(false);
        const selectedCategory = ref(null);
        const selectedTag = ref(null);
        const alwaysApply = ref(false);
        const excludeFromAnalysis = ref(false);
        const alwaysApplyExcludeInclude = ref(false);
        const excludeDialogVisible = ref(false);
        const categories = ref([]);
        function resolveIconPath(iconPath) {
            if (!iconPath)
                return null;
            try {
                return require('@/assets/transactions/' + iconPath.toLowerCase().replace('.svg', '.png'));
            }
            catch {
                return null;
            }
        }
        const CATEGORY_ORDER = ['Income', 'Essentials', 'Lifestyle', 'Future', 'General'];
        function buildCategories(tree) {
            const parents = Array.isArray(tree?.parents) ? [...tree.parents] : [];
            parents.sort((a, b) => {
                const ai = CATEGORY_ORDER.indexOf(a.name);
                const bi = CATEGORY_ORDER.indexOf(b.name);
                return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
            });
            return parents.filter((p) => Array.isArray(p.subClassifications) && p.subClassifications.length > 0).map((p) => {
                const subs = Array.isArray(p.subClassifications) ? p.subClassifications : [];
                const firstIcon = p.iconPath ?? subs.find((s) => s.iconPath)?.iconPath;
                const tags = subs
                    .map((s) => ({ name: s.mgSubClassification || s.mgClassification, classification: s }))
                    .filter((t) => !!t.name);
                return {
                    key: p.name,
                    label: p.name,
                    colour: p.color,
                    iconPath: resolveIconPath(firstIcon),
                    tags,
                };
            }).filter((c) => c.tags.length > 0);
        }
        onMounted(() => {
            getTransactionClassificationTree((tree) => {
                categories.value = buildCategories(tree);
            });
        });
        const selectedCategoryData = computed(() => {
            if (!selectedCategory.value)
                return null;
            return categories.value.find(c => c.key === selectedCategory.value) ?? null;
        });
        const displayTags = computed(() => selectedCategoryData.value?.tags ?? []);
        function selectCategory(key) {
            selectedCategory.value = key;
            selectedTag.value = null;
        }
        const selectedTagData = ref(null);
        function selectTag(tag) {
            selectedTag.value = tag.name;
            selectedTagData.value = tag;
        }
        function resetState() {
            selectedCategory.value = null;
            selectedTag.value = null;
            selectedTagData.value = null;
            alwaysApply.value = false;
            excludeFromAnalysis.value = false;
            alwaysApplyExcludeInclude.value = false;
            excludeDialogVisible.value = false;
            saving.value = false;
        }
        const revertingExclude = ref(false);
        watch(excludeFromAnalysis, () => {
            if (!isOpen.value)
                return;
            if (revertingExclude.value) {
                revertingExclude.value = false;
                return;
            }
            alwaysApplyExcludeInclude.value = false;
            excludeDialogVisible.value = true;
        });
        function confirmAlwaysExcludeInclude(always) {
            alwaysApplyExcludeInclude.value = always;
            excludeDialogVisible.value = false;
        }
        function cancelExcludeChange() {
            revertingExclude.value = true;
            excludeFromAnalysis.value = !excludeFromAnalysis.value;
            excludeDialogVisible.value = false;
        }
        function applyPreselection() {
            if (!isOpen.value)
                return;
            if (!categories.value.length)
                return;
            const c = props.currentClassification;
            if (!c)
                return;
            const tagName = c.mgSubClassification || c.mgClassification;
            if (!tagName)
                return;
            const parent = categories.value.find(cat => cat.tags.some(t => t.name === tagName));
            if (!parent)
                return;
            const tag = parent.tags.find(t => t.name === tagName) ?? null;
            selectedCategory.value = parent.key;
            selectedTag.value = tag?.name ?? null;
            selectedTagData.value = tag;
        }
        watch(isOpen, (open) => {
            if (!open) {
                resetState();
                return;
            }
            const initialExclude = props.currentExcludeFromAnalysis ?? false;
            if (excludeFromAnalysis.value !== initialExclude) {
                revertingExclude.value = true;
                excludeFromAnalysis.value = initialExclude;
            }
            applyPreselection();
        });
        watch(categories, () => {
            if (isOpen.value)
                applyPreselection();
        });
        function applyAndClose() {
            if (saving.value)
                return;
            const classification = selectedTagData.value?.classification;
            if (!classification) {
                isOpen.value = false;
                return;
            }
            saving.value = true;
            emit('apply', {
                classification,
                alwaysApply: alwaysApply.value,
                excludeFromAnalysis: excludeFromAnalysis.value,
                alwaysApplyExcludeInclude: alwaysApplyExcludeInclude.value,
            });
        }
        function openDrawer() { isOpen.value = true; }
        function closeDrawer() { isOpen.value = false; }
        __expose({ openDrawer, closeDrawer });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_dialog = _resolveComponent("v-dialog");
            return (_openBlock(), _createBlock(_component_v_dialog, {
                "model-value": isOpen.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => (isOpen.value = $event)),
                "max-width": "640",
                scrim: "rgba(0, 0, 0, 0.5)",
                opacity: 0.5
            }, {
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("h3", _hoisted_3, _toDisplayString(excludeDialogVisible.value ? (excludeFromAnalysis.value ? 'Exclude' : 'Include') + ' transaction' : 'Change category'), 1),
                            _createElementVNode("button", {
                                class: "cc-close",
                                onClick: closeDrawer
                            }, [
                                _createVNode(_component_v_icon, { size: "18" }, {
                                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                                        _createTextVNode("mdi-close")
                                    ])),
                                    _: 1
                                })
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_4, [
                            (excludeDialogVisible.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                    _createElementVNode("p", _hoisted_6, [
                                        _createTextVNode(" Would you like to always " + _toDisplayString(excludeFromAnalysis.value ? 'exclude' : 'include') + " transactions", 1),
                                        (_ctx.merchantName)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _cache[6] || (_cache[6] = _createTextVNode(" from ")),
                                                _createElementVNode("strong", null, _toDisplayString(_ctx.merchantName), 1)
                                            ], 64))
                                            : _createCommentVNode("", true),
                                        _cache[7] || (_cache[7] = _createTextVNode("? "))
                                    ]),
                                    _createElementVNode("div", _hoisted_7, [
                                        _createElementVNode("button", {
                                            class: "cc-primary-btn",
                                            onClick: _cache[0] || (_cache[0] = ($event) => (confirmAlwaysExcludeInclude(true)))
                                        }, " Yes, always " + _toDisplayString(excludeFromAnalysis.value ? 'exclude' : 'include'), 1),
                                        _createElementVNode("button", {
                                            class: "cc-primary-btn",
                                            onClick: _cache[1] || (_cache[1] = ($event) => (confirmAlwaysExcludeInclude(false)))
                                        }, " No, just this once "),
                                        _createElementVNode("button", {
                                            class: "cc-cancel",
                                            onClick: cancelExcludeChange
                                        }, "Back")
                                    ])
                                ]))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createElementVNode("div", _hoisted_8, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (cat) => {
                                            return (_openBlock(), _createElementBlock("button", {
                                                key: cat.key,
                                                class: _normalizeClass(["cc-cat-item", { dimmed: selectedCategory.value && selectedCategory.value !== cat.key, selected: selectedCategory.value === cat.key }]),
                                                onClick: ($event) => (selectCategory(cat.key))
                                            }, [
                                                _createElementVNode("div", {
                                                    class: "cc-cat-circle",
                                                    style: _normalizeStyle({ background: cat.colour })
                                                }, [
                                                    (cat.iconPath)
                                                        ? (_openBlock(), _createElementBlock("img", {
                                                            key: 0,
                                                            class: "cc-cat-icon",
                                                            src: cat.iconPath
                                                        }, null, 8, _hoisted_10))
                                                        : (_openBlock(), _createBlock(_component_v_icon, {
                                                            key: 1,
                                                            size: "24",
                                                            color: "white"
                                                        }, {
                                                            default: _withCtx(() => _cache[8] || (_cache[8] = [
                                                                _createTextVNode("mdi-tag-outline")
                                                            ])),
                                                            _: 1
                                                        })),
                                                    (selectedCategory.value === cat.key)
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                                                            _createVNode(_component_v_icon, {
                                                                size: "8",
                                                                color: "white"
                                                            }, {
                                                                default: _withCtx(() => _cache[9] || (_cache[9] = [
                                                                    _createTextVNode("mdi-check")
                                                                ])),
                                                                _: 1
                                                            })
                                                        ]))
                                                        : _createCommentVNode("", true)
                                                ], 4),
                                                _createElementVNode("span", {
                                                    class: _normalizeClass(["cc-cat-label", { bold: selectedCategory.value === cat.key }])
                                                }, _toDisplayString(cat.label), 3)
                                            ], 10, _hoisted_9));
                                        }), 128))
                                    ]),
                                    (selectedCategoryData.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                            _cache[11] || (_cache[11] = _createElementVNode("span", { class: "cc-tags-title" }, "Tags", -1)),
                                            _createElementVNode("div", _hoisted_13, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayTags.value, (tag) => {
                                                    return (_openBlock(), _createElementBlock("button", {
                                                        key: tag.name,
                                                        class: _normalizeClass(["cc-tag", { selected: selectedTag.value === tag.name }]),
                                                        onClick: ($event) => (selectTag(tag))
                                                    }, [
                                                        (selectedTag.value === tag.name)
                                                            ? (_openBlock(), _createBlock(_component_v_icon, {
                                                                key: 0,
                                                                size: "14"
                                                            }, {
                                                                default: _withCtx(() => _cache[10] || (_cache[10] = [
                                                                    _createTextVNode("mdi-check-circle")
                                                                ])),
                                                                _: 1
                                                            }))
                                                            : _createCommentVNode("", true),
                                                        _createTextVNode(" " + _toDisplayString(tag.name), 1)
                                                    ], 10, _hoisted_14));
                                                }), 128))
                                            ])
                                        ]))
                                        : _createCommentVNode("", true)
                                ], 64))
                        ]),
                        (!excludeDialogVisible.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                _createElementVNode("div", _hoisted_16, [
                                    _createElementVNode("label", {
                                        class: _normalizeClass(["cc-toggle-row", { dimmed: !selectedTag.value }])
                                    }, [
                                        _cache[12] || (_cache[12] = _createElementVNode("span", { class: "cc-toggle-label" }, "Always apply to this merchant", -1)),
                                        _withDirectives(_createElementVNode("input", {
                                            type: "checkbox",
                                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((alwaysApply).value = $event))
                                        }, null, 512), [
                                            [_vModelCheckbox, alwaysApply.value]
                                        ])
                                    ], 2),
                                    _createElementVNode("label", {
                                        class: _normalizeClass(["cc-toggle-row", { dimmed: !selectedTag.value }])
                                    }, [
                                        _cache[13] || (_cache[13] = _createElementVNode("span", { class: "cc-toggle-label" }, "Exclude from Spending Analysis", -1)),
                                        _withDirectives(_createElementVNode("input", {
                                            type: "checkbox",
                                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((excludeFromAnalysis).value = $event))
                                        }, null, 512), [
                                            [_vModelCheckbox, excludeFromAnalysis.value]
                                        ])
                                    ], 2)
                                ]),
                                _createElementVNode("button", {
                                    class: "cc-primary-btn",
                                    disabled: !selectedTag.value || saving.value,
                                    onClick: applyAndClose
                                }, " Update ", 8, _hoisted_17)
                            ]))
                            : _createCommentVNode("", true)
                    ])
                ]),
                _: 1
            }, 8, ["model-value"]));
        };
    }
});
